import { estimate, isMaterial, isNozzle, type MaterialId, type NozzleId } from "./catalog";

const ACCEPT = [".stl", ".obj", ".3mf", ".step", ".stp"];

function $<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el as T;
}

function apiUrl(path: string) {
  const base = import.meta.env.BASE_URL.replace(/\/?$/, "/");
  return `${base}${path.replace(/^\//, "")}`;
}

export function initQuote() {
  const fileInput = $<HTMLInputElement>("file-input");
  const fileStatus = $("file-status");
  const material = $<HTMLSelectElement>("material");
  const nozzle = $<HTMLSelectElement>("nozzle");
  const price = $("price");
  const weight = $("weight");
  const gramRate = $("gram-rate");
  const nozzleHint = $("nozzle-hint");
  const checkout = $<HTMLButtonElement>("checkout-button");
  const checkoutStatus = $("checkout-status");
  const drop = document.querySelector<HTMLElement>(".upload-card");

  let fileName = "";

  function selectedMaterial(): MaterialId {
    return isMaterial(material.value) ? material.value : "pla";
  }

  function selectedNozzle(): NozzleId {
    return isNozzle(nozzle.value) ? nozzle.value : "0.4";
  }

  function render() {
    const result = estimate(selectedMaterial(), selectedNozzle());
    weight.textContent = String(result.grams);
    price.textContent = result.usd.toFixed(2);
    gramRate.textContent = `$${result.rate.toFixed(2)} / gram`;
    nozzleHint.textContent = {
      "0.2": "Nice finish · more material · higher price",
      "0.4": "Standard nozzle. Price follows the material used.",
      "0.6": "Rough surface · less material · lower price",
    }[selectedNozzle()];
    document.querySelectorAll<HTMLElement>("[data-material]").forEach((item) => {
      item.classList.toggle("active", item.dataset.material === selectedMaterial());
    });
    document.querySelectorAll<HTMLElement>("[data-nozzle]").forEach((item) => {
      item.classList.toggle("active", item.dataset.nozzle === selectedNozzle());
    });
  }

  function takeFile(file: File | undefined) {
    if (!file) return;
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPT.includes(ext)) {
      fileStatus.textContent = "Use STL, OBJ, 3MF or STEP";
      return;
    }
    fileName = file.name;
    const mb = (file.size / 1024 / 1024).toFixed(1);
    fileStatus.textContent = `${file.name} · ${mb} MB ready to print`;
    checkout.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  fileInput.addEventListener("change", () => takeFile(fileInput.files?.[0]));
  material.addEventListener("change", render);
  nozzle.addEventListener("change", render);

  document.querySelectorAll<HTMLElement>("[data-material]").forEach((item) => {
    item.addEventListener("click", () => {
      if (isMaterial(item.dataset.material ?? "")) material.value = item.dataset.material as MaterialId;
      render();
    });
  });
  document.querySelectorAll<HTMLElement>("[data-nozzle]").forEach((item) => {
    item.addEventListener("click", () => {
      if (isNozzle(item.dataset.nozzle ?? "")) nozzle.value = item.dataset.nozzle as NozzleId;
      render();
    });
  });

  if (drop) {
    const on = (event: DragEvent) => {
      event.preventDefault();
      drop.classList.add("is-drop");
    };
    const off = (event: DragEvent) => {
      event.preventDefault();
      drop.classList.remove("is-drop");
    };
    drop.addEventListener("dragenter", on);
    drop.addEventListener("dragover", on);
    drop.addEventListener("dragleave", off);
    drop.addEventListener("drop", (event: DragEvent) => {
      off(event);
      takeFile(event.dataTransfer?.files[0]);
    });
  }

  checkout.addEventListener("click", async () => {
    if (!fileName) {
      checkoutStatus.textContent = "Upload a model first.";
      fileInput.focus();
      return;
    }
    checkout.disabled = true;
    checkoutStatus.textContent = "Opening Stripe…";
    try {
      const response = await fetch(apiUrl("api/checkout.php"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          material: selectedMaterial(),
          nozzle: selectedNozzle(),
          fileName,
        }),
      });
      const data = (await response.json()) as { url?: string; error?: string };
      if (!response.ok || !data.url) {
        checkoutStatus.textContent = data.error || "Checkout is not live yet.";
        checkout.disabled = false;
        return;
      }
      window.location.assign(data.url);
    } catch {
      checkoutStatus.textContent = "Could not reach checkout. Try again.";
      checkout.disabled = false;
    }
  });

  render();
}
